import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Building2,
  Plus,
  Search,
  Edit,
  Trash2,
  Users,
  Gift,
  Calendar,
  Mail,
  User,
  Key,
  Check,
  X,
  Loader2,
  Crown,
  CreditCard,
  Clock,
  Eye,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import axios from 'axios';
import { ADMIN_BASE_URL, hasPermission } from '../config/admin';
import { useAuth } from '../context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

export default function CompanyManagement() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showAddUserDialog, setShowAddUserDialog] = useState(false);
  const [showCompDialog, setShowCompDialog] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);

  // Form states
  const [newCompany, setNewCompany] = useState({
    company_name: '',
    plan: 'professional',
    admin_name: '',
    admin_email: ''
  });

  const [newUser, setNewUser] = useState({
    name: '',
    email: '',
    role: 'user'
  });

  const [compSettings, setCompSettings] = useState({
    is_comped: false,
    comp_until: '',
    comp_forever: false,
    comp_reason: ''
  });

  useEffect(() => {
    fetchCompanies();
  }, []);

  // Reset to page 1 when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const fetchCompanies = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/admin/companies`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCompanies(response.data.companies || []);
    } catch (error) {
      console.error('Failed to fetch companies:', error);
      toast.error('Failed to load companies');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCompany = async () => {
    if (!newCompany.company_name || !newCompany.admin_email || !newCompany.admin_name) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/api/admin/companies`, newCompany, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const message = response.data?.invitation_sent 
        ? 'Company created and invitation email sent!' 
        : 'Company created successfully!';
      toast.success(message);
      setShowCreateDialog(false);
      setNewCompany({ company_name: '', plan: 'professional', admin_name: '', admin_email: '' });
      fetchCompanies();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create company');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddUser = async () => {
    if (!newUser.email || !newUser.name || !selectedCompany) {
      toast.error('Please fill in name and email');
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/api/admin/companies/${selectedCompany.id}/users`, newUser, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const message = response.data?.invitation_sent 
        ? 'User created and invitation email sent!' 
        : 'User added successfully!';
      toast.success(message);
      setShowAddUserDialog(false);
      setNewUser({ name: '', email: '', role: 'user' });
      fetchCompanies();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add user');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateComp = async () => {
    if (!selectedCompany) return;

    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/api/admin/companies/${selectedCompany.id}/comp`, {
        is_comped: compSettings.is_comped,
        comp_until: compSettings.comp_forever ? null : compSettings.comp_until,
        comp_forever: compSettings.comp_forever,
        comp_reason: compSettings.comp_reason
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Comp settings updated!');
      setShowCompDialog(false);
      fetchCompanies();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update comp settings');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteCompany = async (companyId) => {
    if (!window.confirm('Are you sure you want to delete this company? This action cannot be undone.')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/api/admin/companies/${companyId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Company deleted');
      fetchCompanies();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete company');
    }
  };

  const filteredCompanies = companies.filter(c =>
    c.company_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.id?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Pagination calculations
  const totalItems = filteredCompanies.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedCompanies = filteredCompanies.slice(startIndex, endIndex);

  const canManageCompanies = hasPermission(user?.role, 'canManageTenants');
  const canCompUsers = hasPermission(user?.role, 'canCompUsers');

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <Building2 className="w-8 h-8 text-blue-500" />
            Company Management
          </h1>
          <p className="text-slate-400 mt-1">Create and manage companies with users</p>
        </div>
        {canManageCompanies && (
          <Button 
            onClick={() => setShowCreateDialog(true)}
            className="bg-blue-600 hover:bg-blue-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create Company
          </Button>
        )}
      </div>

      {/* Search and Pagination Controls */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <Input
            placeholder="Search companies..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 bg-slate-900 border-slate-700"
          />
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-slate-400">Show:</span>
          <Select value={itemsPerPage.toString()} onValueChange={(v) => { setItemsPerPage(Number(v)); setCurrentPage(1); }}>
            <SelectTrigger className="w-[80px] bg-slate-900 border-slate-700">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-slate-900 border-slate-700">
              <SelectItem value="25">25</SelectItem>
              <SelectItem value="50">50</SelectItem>
              <SelectItem value="100">100</SelectItem>
            </SelectContent>
          </Select>
          <span className="text-sm text-slate-400">of {totalItems}</span>
        </div>
      </div>

      {/* Companies Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {paginatedCompanies.map((company, index) => (
          <motion.div
            key={company.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.05 }}
          >
            <Card className="bg-slate-900 border-slate-800 hover:border-blue-500/50 transition-all">
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                      <Building2 className="w-5 h-5 text-blue-500" />
                    </div>
                    <div>
                      <CardTitle className="text-lg text-white">{company.company_name}</CardTitle>
                      <CardDescription className="text-xs">{company.id}</CardDescription>
                    </div>
                  </div>
                  {company.is_comped && (
                    <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
                      <Gift className="w-3 h-3 mr-1" />
                      Comped
                    </Badge>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-500">Plan:</span>
                    <Badge variant="outline" className="capitalize">{company.plan}</Badge>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-500">Users:</span>
                    <span className="text-white">{company.user_count || 0}</span>
                  </div>
                  {company.is_comped && (
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-slate-500">Comp Until:</span>
                      <span className="text-green-400">
                        {company.comp_forever ? 'Forever' : company.comp_until || 'N/A'}
                      </span>
                    </div>
                  )}
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-500">Created:</span>
                    <span className="text-white">
                      {new Date(company.created_at).toLocaleDateString()}
                    </span>
                  </div>
                </div>

                <div className="flex gap-2 mt-4 pt-4 border-t border-slate-800">
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1"
                    onClick={() => navigate(`${ADMIN_BASE_URL}/companies/${company.id}`)}
                  >
                    <Eye className="w-3 h-3 mr-1" />
                    View
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1"
                    onClick={() => {
                      setSelectedCompany(company);
                      setShowAddUserDialog(true);
                    }}
                  >
                    <Users className="w-3 h-3 mr-1" />
                    Add User
                  </Button>
                  {canCompUsers && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1"
                      onClick={() => {
                        setSelectedCompany(company);
                        setCompSettings({
                          is_comped: company.is_comped || false,
                          comp_until: company.comp_until || '',
                          comp_forever: company.comp_forever || false,
                          comp_reason: company.comp_reason || ''
                        });
                        setShowCompDialog(true);
                      }}
                    >
                      <Gift className="w-3 h-3 mr-1" />
                      Comp
                    </Button>
                  )}
                  {canManageCompanies && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-red-400 hover:text-red-300 hover:bg-red-500/20"
                      onClick={() => handleDeleteCompany(company.id)}
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </div>

      {filteredCompanies.length === 0 && (
        <div className="text-center py-12 text-slate-500">
          <Building2 className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p>No companies found</p>
        </div>
      )}

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between pt-4 border-t border-slate-800">
          <p className="text-sm text-slate-400">
            Showing {startIndex + 1} to {Math.min(endIndex, totalItems)} of {totalItems} companies
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="border-slate-700"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <div className="flex items-center gap-1">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }
                return (
                  <Button
                    key={pageNum}
                    variant={currentPage === pageNum ? "default" : "outline"}
                    size="sm"
                    onClick={() => setCurrentPage(pageNum)}
                    className={currentPage === pageNum ? "bg-blue-600" : "border-slate-700"}
                  >
                    {pageNum}
                  </Button>
                );
              })}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="border-slate-700"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Create Company Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="bg-slate-900 border-slate-800">
          <DialogHeader>
            <DialogTitle className="text-white">Create New Company</DialogTitle>
            <DialogDescription>
              Create a new company. The admin will receive an invitation email to set their password.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Company Name *</Label>
              <Input
                placeholder="Acme Corp"
                value={newCompany.company_name}
                onChange={(e) => setNewCompany({ ...newCompany, company_name: e.target.value })}
                className="bg-slate-800 border-slate-700"
              />
            </div>
            <div className="space-y-2">
              <Label>Plan</Label>
              <Select
                value={newCompany.plan}
                onValueChange={(value) => setNewCompany({ ...newCompany, plan: value })}
              >
                <SelectTrigger className="bg-slate-800 border-slate-700">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  <SelectItem value="professional">Professional ($299/mo)</SelectItem>
                  <SelectItem value="enterprise">Enterprise ($599/mo)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="border-t border-slate-700 pt-4">
              <p className="text-sm text-slate-400 mb-3">Admin User Details</p>
              <p className="text-xs text-blue-400 mb-3">
                <Mail className="w-3 h-3 inline mr-1" />
                An invitation email will be sent to the admin to complete their account setup.
              </p>
              <div className="space-y-3">
                <div className="space-y-2">
                  <Label>Admin Name *</Label>
                  <Input
                    placeholder="John Doe"
                    value={newCompany.admin_name}
                    onChange={(e) => setNewCompany({ ...newCompany, admin_name: e.target.value })}
                    className="bg-slate-800 border-slate-700"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Admin Email *</Label>
                  <Input
                    type="email"
                    placeholder="admin@company.com"
                    value={newCompany.admin_email}
                    onChange={(e) => setNewCompany({ ...newCompany, admin_email: e.target.value })}
                    className="bg-slate-800 border-slate-700"
                  />
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleCreateCompany}
              disabled={submitting}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                <>
                  <Mail className="w-4 h-4 mr-2" />
                  Create & Send Invite
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add User Dialog */}
      <Dialog open={showAddUserDialog} onOpenChange={setShowAddUserDialog}>
        <DialogContent className="bg-slate-900 border-slate-800">
          <DialogHeader>
            <DialogTitle className="text-white">Add User to {selectedCompany?.company_name}</DialogTitle>
            <DialogDescription>
              The user will receive an invitation email to set their password and complete onboarding.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Name *</Label>
              <Input
                placeholder="Jane Doe"
                value={newUser.name}
                onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                className="bg-slate-800 border-slate-700"
              />
            </div>
            <div className="space-y-2">
              <Label>Email *</Label>
              <Input
                type="email"
                placeholder="user@company.com"
                value={newUser.email}
                onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                className="bg-slate-800 border-slate-700"
              />
            </div>
            <div className="space-y-2">
              <Label>Role</Label>
              <Select
                value={newUser.role}
                onValueChange={(value) => setNewUser({ ...newUser, role: value })}
              >
                <SelectTrigger className="bg-slate-800 border-slate-700">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  <SelectItem value="user">User</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddUserDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleAddUser}
              disabled={submitting}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                <>
                  <Mail className="w-4 h-4 mr-2" />
                  Send Invitation
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Comp Settings Dialog */}
      <Dialog open={showCompDialog} onOpenChange={setShowCompDialog}>
        <DialogContent className="bg-slate-900 border-slate-800">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Gift className="w-5 h-5 text-green-500" />
              Comp Settings - {selectedCompany?.company_name}
            </DialogTitle>
            <DialogDescription>
              Give this company free access to the platform
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="is_comped"
                checked={compSettings.is_comped}
                onCheckedChange={(checked) => setCompSettings({ ...compSettings, is_comped: checked })}
              />
              <Label htmlFor="is_comped">Enable Complimentary Access</Label>
            </div>

            {compSettings.is_comped && (
              <>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="comp_forever"
                    checked={compSettings.comp_forever}
                    onCheckedChange={(checked) => setCompSettings({ ...compSettings, comp_forever: checked })}
                  />
                  <Label htmlFor="comp_forever">Comp Forever (no expiration)</Label>
                </div>

                {!compSettings.comp_forever && (
                  <div className="space-y-2">
                    <Label>Comp Until Date</Label>
                    <Input
                      type="date"
                      value={compSettings.comp_until}
                      onChange={(e) => setCompSettings({ ...compSettings, comp_until: e.target.value })}
                      className="bg-slate-800 border-slate-700"
                    />
                  </div>
                )}

                <div className="space-y-2">
                  <Label>Reason (internal note)</Label>
                  <Input
                    placeholder="e.g., Beta tester, Partner, Friend..."
                    value={compSettings.comp_reason}
                    onChange={(e) => setCompSettings({ ...compSettings, comp_reason: e.target.value })}
                    className="bg-slate-800 border-slate-700"
                  />
                </div>
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCompDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleUpdateComp}
              disabled={submitting}
              className="bg-green-600 hover:bg-green-700"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save Comp Settings'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
